import { useState } from 'react';
import { components } from '@lifeserver/models/lifev2';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import _ from 'lodash';

import Modal from '../shared/Modal';
import { LanguageType } from '@lifeserver/shared';

import { handleEnvAuth } from '../utils/envaccess';
import {
    fetchDataFromPath,
    postDataForPath,
    putDataForPath,
} from '../utils/envcopy';
import { CAROUSELS_BASE_PATH, PACKS_BASE_PATH } from '../utils/pathsConstants';

import { Carousel, CreateCarousel } from '../types/carouselTypes';

const QUERY_KEY_CAROUSELS = 'carousels';
const QUERY_KEY_CAROUSEL = 'carousel';

async function listCarousels() {
    const { data } = await axios.get<components['schemas']['CarouselDTO'][]>(
        `/api/life/api/admin/carousels`,
    );
    return data;
}

async function getCarousel(id: number) {
    const { data } = await axios.get<components['schemas']['CarouselDTO']>(
        `/api/life/api/admin/carousels/${id}?queryType=id`,
    );
    return data;
}

export function useCarousels() {
    const { isPending, error, data } = useQuery({
        queryKey: [QUERY_KEY_CAROUSELS],
        queryFn: listCarousels,
    });

    return { carousels: data, isPending, error };
}

export function useCarousel(id: number) {
    const { isPending, error, data } = useQuery({
        queryKey: [QUERY_KEY_CAROUSEL, id],
        queryFn: () => getCarousel(id),
        enabled: !isNaN(id),
    });

    return { carousel: data, isPending: isPending && !isNaN(id), error };
}

export function useCarouselTranslations(
    carouselName?: Carousel['carouselName'],
) {
    async function getCarouselTranslations() {
        const {
            data: { name },
        } = await axios.get<{ name: Record<LanguageType, string> }>(
            `/api/${PACKS_BASE_PATH}/getpackfromsource/${carouselName}`,
        );
        return name;
    }

    const { data, isPending, error } = useQuery({
        queryKey: ['carouselTranslations', carouselName],
        queryFn: getCarouselTranslations,
        enabled: carouselName !== undefined,
    });

    return {
        carouselTranslations: data,
        isPending,
        error,
    };
}

export function useCarouselFeaturedPacks() {
    async function getCarouselFeaturedPacks() {
        const { data } = await axios.get<string[]>(
            `/api/${CAROUSELS_BASE_PATH}/featured`,
        );
        return data;
    }

    const queryClient = useQueryClient();
    const { mutate, data, isPending, error } = useMutation({
        mutationFn: getCarouselFeaturedPacks,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['carouselFeaturedPacks'],
            });
        },
    });

    return {
        featuredPacks: data,
        fetch: mutate,
        isPending,
        error,
    };
}

export function useCreateCarousel() {
    async function createCarousel(carousel: CreateCarousel) {
        const { data } = await axios.post<components['schemas']['CarouselDTO']>(
            `/api/life/api/admin/carousels`,
            carousel,
        );
        return data;
    }

    const queryClient = useQueryClient();
    const { mutate, data, isPending, isSuccess, error } = useMutation({
        mutationFn: createCarousel,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [QUERY_KEY_CAROUSELS],
            });
        },
    });

    return {
        createCarousel: mutate,
        createdCarousel: data,
        isPending,
        isSuccess,
        error,
    };
}

type UpdateCarouselRequest = {
    carouselId: Carousel['carouselId'];
    carousel: CreateCarousel;
};

export function useUpdateCarousel() {
    async function updateCarousel(
        updateCarouselRequest: UpdateCarouselRequest,
    ) {
        const { carouselId, carousel } = updateCarouselRequest;
        await axios.put(
            `/api/life/api/admin/carousels/${carouselId}`,
            carousel,
        );
    }

    const queryClient = useQueryClient();
    const { mutate, isPending, isSuccess, error } = useMutation({
        mutationFn: updateCarousel,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [QUERY_KEY_CAROUSELS],
            });
        },
    });

    return {
        updateCarousel: mutate,
        isPending,
        isSuccess,
        error,
    };
}

export function useUpdateCarouselOrder() {
    async function updateCarouselOrder(carouselIds: Carousel['carouselId'][]) {
        await axios.put(`/api/life/api/admin/carousels/priorities`, {
            ids: carouselIds,
        });
    }

    const queryClient = useQueryClient();
    const { mutate, error } = useMutation({
        mutationFn: updateCarouselOrder,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [QUERY_KEY_CAROUSELS],
            });
        },
    });

    return {
        updateCarouselOrder: mutate,
        error,
    };
}

type ToggleActiveCarouselRequest = {
    carouselId: Carousel['carouselId'];
    isSetActive: boolean;
};

export function useToggleActiveCarousel() {
    async function toggleActiveCarousel(
        toggleActiveCarouselRequest: ToggleActiveCarouselRequest,
    ) {
        const { carouselId, isSetActive } = toggleActiveCarouselRequest;
        await axios.put(
            `/api/life/api/admin/carousels/${carouselId}/toggleactive`,
            {
                setActive: isSetActive,
            },
        );
    }

    const queryClient = useQueryClient();
    const { mutate, error } = useMutation({
        mutationFn: toggleActiveCarousel,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [QUERY_KEY_CAROUSELS],
            });
        },
    });

    return {
        toggleActiveCarousel: mutate,
        error,
    };
}

export function useCopyCarousel() {
    const [loadingMessage, setLoadingMessage] = useState<string>();

    const copyCarousel = async (carouselName: Carousel['carouselName']) => {
        setLoadingMessage('Copying carousel...');

        const env = await handleEnvAuth();
        if (!env) {
            return setLoadingMessage(undefined);
        }

        // Get and compare carousels
        const currentCarousel = await fetchDataFromPath(
            `/api/life/api/admin/carousels/${carouselName}`,
        );
        const refCarousel = await fetchDataFromPath(
            `${env.basepath}/api/life/api/admin/carousels/${carouselName}`,
        );

        let isConfirmed: boolean;
        if (_.isEmpty(refCarousel)) {
            // No carousel in target environment, show confirm modal
            isConfirmed = await Modal.confirm({
                heading: 'Copy carousel',
                text: `No carousel with name '${carouselName}' found in ${env.name}, do you want to make a copy?`,
                okLabel: 'Yes do it!',
            });
        } else {
            // Carousel found, show compare modal
            isConfirmed = await Modal.compare({
                heading: 'Compare carousels',
                text: 'The following differences were found',
                okLabel: 'Copy',
                object1: refCarousel,
                object2: currentCarousel,
            });
        }
        if (!isConfirmed) {
            return setLoadingMessage(undefined);
        }

        // Prepare carousel content before copy
        setLoadingMessage('Preparing carousel content...');
        // Check which packs exist in target env by fetching the pack list once
        const { packs: targetEnvPacks } = await fetchDataFromPath(
            `${env.basepath}/api/${PACKS_BASE_PATH}/list/all`,
        );
        const targetPackIds = new Set(
            targetEnvPacks?.map((p: { packId: string }) => p.packId) || [],
        );

        const items = currentCarousel.items.filter((item: { packId: string }) =>
            targetPackIds.has(item.packId),
        );

        if (currentCarousel.items.length) {
            // Show modal here of how many packs that were found
            isConfirmed = await Modal.confirm({
                heading: 'Packs found',
                text: `${items.length} out of ${currentCarousel.items.length} packs were found in ${env.name}, do you still want to continue?`,
                okLabel: 'Continue',
            });
            if (!isConfirmed) {
                return setLoadingMessage(undefined);
            }
        }

        const newCarousel = { ...currentCarousel, items, active: false };
        // Finally, copy carousel to target environment
        setLoadingMessage(`Saving carousel to ${env.name}...`);
        const hasCopiedSuccessfully = _.isEmpty(refCarousel)
            ? await postDataForPath(
                  { data: newCarousel },
                  `${env.basepath}/api/life/api/admin/carousels`,
              )
            : await putDataForPath(
                  { data: newCarousel },
                  `${env.basepath}/api/life/api/admin/carousels/${refCarousel.carouselId}?queryType=id`,
              );

        setLoadingMessage(undefined);
        if (hasCopiedSuccessfully) {
            await Modal.alert({
                heading: 'Copying done!',
                text: `Woho, the carousel with name '${currentCarousel.carouselName}' was successfully stored in ${env.name}!`,
            });
        } else {
            await Modal.alert({
                heading: 'On no!',
                text: `Something went wrong when trying to copy carousel with name '${currentCarousel.carouselName}' to ${env.name}!`,
            });
        }
    };

    return { copyCarousel, loadingMessage };
}

export function useDeleteCarousel() {
    async function deleteCarousel(carouselId: Carousel['carouselId']) {
        await axios.delete(`/api/life/api/admin/carousels/${carouselId}`);
    }

    const queryClient = useQueryClient();
    const { mutate, isSuccess, error } = useMutation({
        mutationFn: deleteCarousel,
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: [QUERY_KEY_CAROUSELS],
            });
        },
    });

    return { deleteCarousel: mutate, isSuccess, error };
}
